import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { useToast } from '../hooks/use-toast';
import { Settings, Plus, Trash2, Edit3, Upload, Download, Save } from 'lucide-react';
import { parseCSV } from '../data/mockData';

const CustomizeView = ({ userVariations, onUpdateVariations, onImportMoves, userMoves = [] }) => {
  const { toast } = useToast();
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newVariationText, setNewVariationText] = useState('');
  const [csvFile, setCsvFile] = useState(null);

  // Ajout d'un hook pour détecter le thème
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.body.classList.contains('light') ? 'light' : 'dark';
    }
    return 'dark';
  });
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(document.body.classList.contains('light') ? 'light' : 'dark');
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const handleAddCategory = () => {
    if (!newCategoryName.trim()) {
      toast({
        title: "Category Name Required",
        description: "Please enter a name for the new category.",
        variant: "destructive"
      });
      return;
    }

    const categoryKey = newCategoryName.toLowerCase().replace(/\s+/g, '');
    if (userVariations[categoryKey]) {
      toast({
        title: "Category Exists",
        description: "A category with this name already exists.",
        variant: "destructive"
      });
      return;
    }

    onUpdateVariations({
      ...userVariations,
      [categoryKey]: []
    });

    setNewCategoryName('');
    toast({
      title: "Category Added! 🎉",
      description: `${newCategoryName} category has been created.`
    });
  };

  const handleDeleteCategory = (categoryKey) => {
    const newVariations = { ...userVariations };
    delete newVariations[categoryKey];
    onUpdateVariations(newVariations);
    
    toast({
      title: "Category Deleted",
      description: "Category and all its variations have been removed."
    });
  };

  const handleUpdateCategoryVariations = (categoryKey) => {
    const variations = newVariationText
      .split(',')
      .map(v => v.trim())
      .filter(v => v);

    onUpdateVariations({
      ...userVariations,
      [categoryKey]: variations
    });

    setEditingCategory(null);
    setNewVariationText('');
    
    toast({
      title: "Variations Updated! ✨",
      description: `${variations.length} variations saved for ${categoryKey}.`
    });
  };

  const startEditingCategory = (categoryKey) => {
    setEditingCategory(categoryKey);
    setNewVariationText(userVariations[categoryKey].join(', '));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvText = e.target.result;
          const allMoves = parseCSV(csvText);
          const existingNames = new Set(
            userMoves
              .filter(m => m && m.name != null)
              .map(m => String(m.name).trim().toLowerCase())
          );
          const uniqueMoves = allMoves.filter(
            m => m && m.name != null && !existingNames.has(String(m.name).trim().toLowerCase())
          );
          const ignored = allMoves.filter(
            m => m && m.name != null && existingNames.has(String(m.name).trim().toLowerCase())
          );
          const existingNamesList = Array.from(existingNames);
          onImportMoves(uniqueMoves);
          console.log('Noms de moves déjà existants (pour le filtrage) :', existingNamesList);
          toast({
            title: "CSV Import Successful! 📁",
            description: `${uniqueMoves.length} moves imported from ${file.name}.` + (ignored.length > 0 ? `\n${ignored.length} doublons ignorés : ${ignored.map(m => m.name).join(', ')}` : '') + (existingNamesList.length > 0 ? `\nMoves existants : ${existingNamesList.join(', ')}` : ''),
          });
          setCsvFile(null);
          event.target.value = '';
        } catch (error) {
          console.error('Erreur lors de l\'import CSV :', error);
          toast({
            title: "Import Failed",
            description: `There was an error processing the CSV file : ${error.message || error}`,
            variant: "destructive"
          });
        }
      };
      reader.readAsText(file);
    } else {
      toast({
        title: "Invalid File",
        description: "Please select a valid CSV file.",
        variant: "destructive"
      });
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = [
      ['name', 'type', 'videoLink', 'origin', 'description'],
      ['Sample Move', 'toprock', 'https://youtube.com/example', 'Sample Origin', 'Sample description'],
      ['Another Move', 'footwork', '', 'User Created', 'Another sample move']
    ];
    
    const csvContent = sampleData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'crewmate_moves_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Template Downloaded! 📥",
      description: "Use this template to format your moves for import."
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-2">
          Customize Your Experience
        </h2>
        <p className="text-muted-foreground">
          Personalize your variations and import custom moves
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Variations Customization */}
        <Card className={`border-4 border-2 bg-white text-black`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Custom Variations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Add New Category */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Add New Category</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Category name..."
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className={`bg-white text-black border-black focus:ring-2 focus:ring-green-500 transition-all duration-200`}
                />
                <Button
                  onClick={handleAddCategory}
                  className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <Separator />

            {/* Existing Categories */}
            <div className="space-y-4">
              {Object.entries(userVariations).map(([categoryKey, variations]) => (
                <div key={categoryKey} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm text-gray-700 capitalize">
                      {categoryKey.replace(/([A-Z])/g, ' $1').trim()}
                    </h4>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEditingCategory(categoryKey)}
                        className="h-8 w-8 p-0 hover:bg-blue-50"
                      >
                        <Edit3 className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteCategory(categoryKey)}
                        className="h-8 w-8 p-0 hover:bg-red-50 text-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {editingCategory === categoryKey ? (
                    <div className="space-y-2">
                      <Textarea
                        value={newVariationText}
                        onChange={(e) => setNewVariationText(e.target.value)}
                        placeholder="Enter variations separated by commas..."
                        rows={3}
                        className={`bg-white text-black border-black focus:ring-2 focus:ring-green-500 resize-none`}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleUpdateCategoryVariations(categoryKey)}
                          className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
                        >
                          <Save className="w-3 h-3 mr-1" />
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingCategory(null);
                            setNewVariationText('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {variations.length > 0 ? (
                        variations.map(variation => (
                          <Badge 
                            key={variation} 
                            variant="outline" 
                            className={`hover:bg-green-50 transition-colors duration-200 ${theme === 'light' ? 'bg-white text-black border-black' : ''}`}
                          >
                            {variation}
                          </Badge>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground italic">
                          No variations yet. Click edit to add some!
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CSV Import */}
        <Card className={`border-4 border-2 bg-white text-black`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Import Moves
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 transition-all duration-200">
              <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-sm text-muted-foreground mb-4">
                Upload a CSV file to import multiple moves at once
              </p>
              <div className="space-y-3">
                {/* DEBUG: input file visible pour test */}
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  id="csv-upload-debug"
                  className="block w-full border p-2 mt-2"
                />
                <Button as="span" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 cursor-pointer w-full">
                  Choose CSV File
                </Button>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-700">CSV Format</h4>
              <div className="bg-gray-50 p-4 rounded-lg text-sm font-mono">
                <div className="text-gray-600 mb-2">Required columns:</div>
                <div>name, type, videoLink, origin, description</div>
              </div>
              <Button
                variant="outline"
                onClick={downloadSampleCSV}
                className="w-full hover:bg-blue-50 transition-all duration-200"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Import Tips</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Ensure your CSV has the required column headers</li>
                <li>• Move types: toprock, footwork, powermoves, freezes, transitions</li>
                <li>• Video links are optional but recommended</li>
                <li>• Large files may take a moment to process</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomizeView;